import { Link, useLocation } from "wouter";
import { useUser, useLogout, useTotalStaff } from "./Use-auth";
import {
  LayoutDashboard,
  UserRoundCog,
  FolderGit,
  FolderCog,
  ArrowUpAZ,
  UserStar,
  ScanLine,
  ChartBarStacked,
  BanknoteArrowDown,
  Triangle,
  Mail,
  Tent,
  CircleUserRound,
  Settings,
  LogOut,
  X,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useEffect, useState } from "react";

export function Sidebar({ open, setOpen }) {
  const { data: user } = useUser();
  const { data: total } = useTotalStaff();
  const { mutate: logout } = useLogout();
  const [location] = useLocation();
  const cleanLocation = location.split("?")[0];
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    if (user && total) {
      const currentUserFromStaff = total.find(
        (emp) => emp.employeeId === user.userId
      );

      setProfileImage(
        user.workInformation?.image ||
        user.image ||
        currentUserFromStaff?.image ||
        null
      );
    }
  }, [user, total]);

  const getImageUrl = (path) => {
    if (!path) return null;

    if (path.startsWith("http") || path.startsWith("blob:")) {
      return path;
    }

    const BASE_URL = process.env.REACT_APP_API_URL;

    return `${BASE_URL}${path}`;
  };

  if (!user) return null;

  const links = [
    {
      href:
        user.department === "Admin"
          ? "/admin"
          : user.department === "Management"
            ? "/management"
            : user.department === "HR"
              ? "/hr-dashboard"
              : user.department === "Sales"
                ? "/sales-dashboard"
                : "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      departments: ["Admin", "HR", "Sales", "Design", "Development", "Management"],
    },
    { href: "/myTeam", label: "My Team", icon: UserRoundCog, departments: ["Admin", "HR"] },
    { href: "/projects", label: "Projects", icon: FolderGit, departments: ["Admin", "Management"] },
    { href: "/lead-contacts", label: user.department === "Sales" ? "Contacts" : "Sales", icon: FolderCog, departments: ["Sales", "Admin", "Management"] },
    { href: "/lead", label: "Leads", icon: ArrowUpAZ, departments: ["Sales"] },
    { href: "/active-contacts", label: "Won Contact", icon: UserStar, departments: ["Sales"] },
    { href: "/hr", label: "HR Dashboard", icon: ScanLine, departments: ["Admin"] },
    { href: "/category", label: "Project Records", icon: ChartBarStacked, departments: ["Admin", "Management"] },
    { href: "/project", label: "Projects", icon: FolderGit, departments: ["Design", "Development"] },
    { href: "/leave-tracking", label: "Attendance", icon: BanknoteArrowDown, departments: ["Admin", "Management", "HR", "Sales", "Design", "Development"] },
    { href: "/expenses", label: "Expenses", icon: BanknoteArrowDown, departments: ["HR"] },
    { href: "/payRoll", label: "Payroll", icon: BanknoteArrowDown, departments: ["HR"] },
    { href: "/hiring", label: "Hiring", icon: Triangle, departments: ["HR"] },
    { href: "/letters", label: "Letters", icon: Mail, departments: ["HR", "Admin"] },
    { href: "/policies", label: "Policies", icon: Tent, departments: ["Admin", "Management", "HR", "Sales", "Design", "Development"] },
    { href: "/account", label: "Account", icon: CircleUserRound, departments: ["Admin", "Management", "HR", "Sales", "Design", "Development"] },
    { href: "/setting", label: "Settings", icon: Settings, departments: ["Admin", "Management"] },
  ];

  const filteredLinks = links.filter(link => link.departments.includes(user.department));
  const mainLinks = filteredLinks.filter(link => link.href !== "/account" && link.href !== "/setting");
  const bottomLinks = filteredLinks.filter(link => link.href === "/account" || link.href === "/setting");

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[9998] lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={cn(
          "h-screen w-52 sm:w-64 flex flex-col fixed lg:sticky top-0 transition-transform duration-300 shadow-xl",
          open ? "translate-x-0 z-[9999]" : "-translate-x-full z-[9999]",
          "lg:translate-x-0 lg:z-50"
        )}
        style={{ backgroundColor: "#ffffff" }}
      >

        <div className="lg:hidden flex justify-end p-0 sm:p-3">
          <button
            onClick={() => setOpen(false)}
            className="p-1 sm:p-2 rounded-md hover:bg-gray-100 transition"
          >
            <X size={18} className="text-gray-600 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Logo */}
        <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-5 py-2 sm:py-4 border-b border-gray-200">
          <img
            src="https://www.hubsyntax.com/Hubsyntax-logo.png"
            alt="HubSyntax Logo"
            className="w-7 h-7 sm:w-10 sm:h-10 rounded-lg"
          />
          <h1 className="text-sm sm:text-xl font-bold text-gray-800">HubSyntax</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 sm:px-4 sm:py-2 space-y-1">
          {mainLinks.map(link => {
            const isActive = cleanLocation === link.href || cleanLocation.startsWith(link.href + "/");
            return (
              <Link key={link.href} href={link.href}>
                <div
                  className={cn(
                    "flex items-center gap-3 px-2 py-2 sm:px-4 sm:py-2 rounded sm:rounded-lg cursor-pointer text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200",
                    isActive && "bg-blue-100 text-blue-600 font-semibold shadow"
                  )}
                  onClick={() => setOpen && setOpen(false)}
                >
                  <link.icon className=" w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-[13px] sm:text-sm">{link.label}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Links */}
        {bottomLinks.length > 0 && (
          <div className="px-2 py-2 sm:px-4 sm:py-2 border-t border-gray-200 mt-auto space-y-1">
            {bottomLinks.map(link => {
              const isActive = cleanLocation === link.href;
              return (
                <Link key={link.href} href={link.href}>
                  <div
                    className={cn(
                      "flex items-center gap-3 px-2 py-2 sm:px-4 sm:py-2 rounded sm:rounded-lg cursor-pointer text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200",
                      isActive && "bg-blue-100 text-blue-600 font-semibold shadow"
                    )}
                    onClick={() => setOpen && setOpen(false)}
                  >
                    <link.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-[13px] sm:text-sm">{link.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Profile Section */}
        <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-2 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {profileImage ? (
              <img
                src={getImageUrl(profileImage)}
                alt="profile"
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-semibold text-gray-700">
                {(user.fullName || "U")
                  .split(" ")
                  .map(n => n[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className=" text-[10px] sm:text-sm font-semibold truncate text-gray-800">{user.fullName}</p>
              <p className="text-[8px] sm:text-xs text-gray-500 truncate">{user.department}</p>
            </div>
          </div>
          <button
            onClick={() => logout()}
            className="p-2 rounded-md hover:bg-red-50 transition"
          >
            <LogOut className="text-red-500 hover:text-red-600 w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </aside>
    </>
  );
}